import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";

import { InvalidOperationException } from "../../exceptions/exceptions";

import { Language } from "./language.enum";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  private readonly _window: Window | null;

  constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    this._window = this._document.defaultView;
  }

  getLanguage() {
    if (!this._window) {
      throw new InvalidOperationException(
        "Was not able to find a global window object. This could be a side effect when changing to server side rendering (SSR).",
      );
    }

    const pathName = this._window.location.pathname;

    const groups = LANGUAGE_REGEX.exec(pathName)?.groups;

    if (groups) {
      const language = groups["language"];

      switch (language) {
        case Language.DE:
          return Language.DE;
        case Language.FR:
          return Language.FR;
        case Language.IT:
          return Language.IT;
      }
    }

    throw new InvalidOperationException(
      `Was not able to retrieve the language from path '${pathName}'. There should always be a language in the url available.`,
    );
  }

  setLanguage(language: Language) {
    if (!this._window) {
      throw new InvalidOperationException(
        "Was not able to find a global window object. This could be side effect when changing server rendering (SSR).",
      );
    }

    const urlPathNamePart = this._window.location.pathname;
    const urlSearchPart = this._window.location.search;

    this._window.location.assign(
      urlPathNamePart.replace(
        `/${this.getLanguage().toString()}/`,
        `/${language.toString()}/`,
      ) + urlSearchPart,
    );
  }

  getAcceptLanguageHttpHeaderValue() {
    const language = this.getLanguage();

    switch (language) {
      case Language.DE:
        return AcceptLanguageHeaderValue.DE;
      case Language.FR:
        return AcceptLanguageHeaderValue.FR;
      case Language.IT:
        return AcceptLanguageHeaderValue.IT;
    }
  }

  /**
   * Update the lang attribute on the html tag.
   * @example <html lang="de">
   */
  updateHtmlTagLanguage() {
    const language = this.getLanguage();
    document.documentElement.lang = language;
  }
}

// https://regex101.com/r/Rstdl9/1
const LANGUAGE_REGEX = /^\/(?<language>\w+)\/?/;

enum AcceptLanguageHeaderValue {
  DE = "de",
  FR = "fr",
  IT = "it",
}
